<template>
  <div class="notification-wrapper">
    <v-menu
      v-model="showNotifications"
      offset-y
      :close-on-content-click="false"
      transition="scale-transition"
      nudge-bottom="8"
    >
      <template #activator="{ props }">
        <button
          class="notification-btn"
          :class="{ 'notification-btn--active': showNotifications }"
          v-bind="props"
          @click.stop="toggleMenu"
        >
          <v-icon size="18" :color="showNotifications ? 'primary' : 'grey darken-2'">
            mdi-bell-outline
          </v-icon>

          <span class="notification-date">{{ formattedDate }}</span>

          <span v-if="unreadCount > 0" class="notification-count-badge">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </button>
      </template>

      <v-list class="notification-list-dropdown" style="width: 360px">
        <div class="notification-list-header px-4 py-3">
          <span class="notification-list-title">Notificações</span>
          <div class="d-flex align-center" style="gap: 8px">
            <span class="notification-list-subtitle">
              {{ unreadCount }} {{ unreadCount === 1 ? 'nova' : 'novas' }}
            </span>
            <button
              v-if="unreadCount > 0"
              class="mark-all-btn"
              @click.stop="onMarkAllAsRead"
            >
              Marcar todas
            </button>
            <button
              v-if="items.length > 0"
              class="clear-all-btn"
              @click.stop="onClearAll"
            >
              Limpar todas
            </button>
          </div>
        </div>

        <v-divider></v-divider>

        <div v-if="loading && items.length === 0" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" size="24" />
        </div>

        <div
          v-else-if="items.length === 0"
          class="text-center py-6"
        >
          <v-icon color="grey lighten-1" size="32">mdi-bell-check-outline</v-icon>
          <p class="caption grey--text mt-2 mb-0">Nenhuma notificação</p>
        </div>

        <v-list-item-group v-else>
          <v-list-item
            v-for="notification in items"
            :key="notification.id"
            class="notification-item"
            :class="{ 'notification-item--unread': !notification.is_read }"
            @click="onClickNotification(notification)"
          >
            <v-list-item-icon class="mr-3">
              <v-icon :color="iconColor(notification)" small>
                {{ iconName(notification) }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="notification-item-title">
                {{ notification.title }}
                <span
                  v-if="!notification.is_read"
                  class="unread-dot"
                ></span>
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="notification.body"
                class="notification-item-body"
              >
                {{ notification.body }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="notification-item-date">
                {{ relativeTime(notification.created_at) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action class="notification-item-action my-0 ml-2">
              <v-tooltip left>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="notification-remove-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="onRemove(notification)"
                  >
                    <v-icon x-small color="grey">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
                <span>Remover</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatDate } from "@/utils/datetime";
import {
  getNotificationMeta,
  getNotificationDestino,
} from "@/composables/useNotificationTypes";

export default {
  name: "NotificationBadge",

  data() {
    return {
      showNotifications: false,
      dateInterval: null,
    };
  },

  computed: {
    ...mapGetters(["currentDate"]),
    ...mapGetters("notifications", ["items", "unreadCount"]),

    loading() {
      return this.$store.state.notifications.loading;
    },

    formattedDate() {
      const today = this.currentDate || new Date();
      const daysOfWeek = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
      const monthNames = [
        "JAN", "FEV", "MAR", "ABR", "MAI", "JUN",
        "JUL", "AGO", "SET", "OUT", "NOV", "DEZ",
      ];
      const day = daysOfWeek[today.getDay()];
      const dayOfMonth = String(today.getDate()).padStart(2, "0");
      const month = monthNames[today.getMonth()];
      return `${day}, ${dayOfMonth} de ${month}`;
    },
  },

  mounted() {
    this.startUpdatingDate();
    if (!this.$store.state.notifications.loaded) {
      this.$store.dispatch("notifications/fetch").catch(() => {});
    }
  },

  beforeDestroy() {
    if (this.dateInterval) clearInterval(this.dateInterval);
  },

  methods: {
    toggleMenu() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        this.$store.dispatch("notifications/fetch").catch(() => {});
      }
    },

    iconName(notification) {
      return getNotificationMeta(notification.type).icon;
    },

    iconColor(notification) {
      return getNotificationMeta(notification.type).color;
    },

    relativeTime(iso) {
      if (!iso) return "";
      const now = Date.now();
      const t = new Date(iso).getTime();
      const sec = Math.max(1, Math.floor((now - t) / 1000));
      if (sec < 60) return "agora";
      const min = Math.floor(sec / 60);
      if (min < 60) return `${min}m`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h`;
      const days = Math.floor(hr / 24);
      if (days < 7) return `${days}d`;
      return formatDate(iso);
    },

    async onClickNotification(notification) {
      this.showNotifications = false;

      if (!notification.is_read) {
        this.$store
          .dispatch("notifications/markAsRead", notification.id)
          .catch(() => {});
      }

      // salvia-850: este método NÃO conhece tipo de notificação. Quem diz para
      // onde o clique leva é o registry (useNotificationTypes); aqui só se
      // despacha a forma do destino. Antes havia um `if` por prefixo de tipo
      // para os recados, que era uma segunda porta para a mesma ação — o
      // padrão que gerou os bugs 823/826/790.
      const destino = getNotificationDestino(notification);
      if (!destino) return;

      if (destino.tipo === "evento") {
        this.$root.$emit(destino.nome, destino.payload);
        return;
      }

      if (destino.tipo === "rota") {
        try {
          await this.$router.push(destino.rota);
        } catch (e) {
          if (e?.name !== "NavigationDuplicated") {
            // ignora navegação para a mesma rota
          }
        }
      }
    },

    async onMarkAllAsRead() {
      try {
        await this.$store.dispatch("notifications/markAllAsRead");
      } catch (e) {
        // silencioso
      }
    },

    async onRemove(notification) {
      try {
        await this.$store.dispatch("notifications/remove", notification.id);
      } catch (e) {
        // a action já ressincroniza em caso de erro
      }
    },

    async onClearAll() {
      const ok = await this.$confirm({
        type: "delete",
        title: "Limpar notificações",
        message: "Remover todas as notificações da lista?",
        hint: "Elas sairão da sua lista de notificações.",
        // salvia-776: a UNICA exclusao do sistema sem motivo obrigatorio.
        // Isto nao e registro: e limpar uma caixa de avisos ja lidos, como
        // limpar notificacao do celular. Pedir justificativa aqui ensinaria o
        // usuario a digitar qualquer coisa por reflexo -- e esse reflexo
        // contaminaria os outros 34 pontos, onde o motivo importa de verdade.
        exigeMotivo: false,
      });
      if (!ok) return;

      this.showNotifications = false;
      try {
        await this.$store.dispatch("notifications/clearAll");
      } catch (e) {
        // a action já ressincroniza em caso de erro
      }
    },

    startUpdatingDate() {
      this.dateInterval = setInterval(() => {
        const now = new Date();
        const last = this.currentDate ? new Date(this.currentDate) : new Date();
        if (now.getDate() !== last.getDate()) {
          this.$store.commit("setCurrentDate");
        }
      }, 60000);
    },
  },
};
</script>

<style scoped>
.notification-wrapper {
  display: flex;
  align-items: center;
}

.notification-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  font-size: 14px;
  border: 1.5px solid #ccc;
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  color: var(--ds-gray-mid);
  position: relative;
}

.notification-btn:hover {
  background-color: #f4f4f4;
  border-color: var(--ds-gray-light);
}

.notification-btn--active {
  background-color: #e8f0fe;
  border-color: var(--ds-blue-accent);
  color: var(--ds-blue-accent);
}

.notification-date {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.notification-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #ff3b30;
  color: var(--ds-white);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.2px;
  box-shadow: 0 1px 3px rgba(255, 59, 48, 0.4);
}

.notification-list-dropdown {
  border-radius: 12px !important;
  overflow: hidden;
  max-height: 480px;
  overflow-y: auto;
}

.notification-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification-list-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-gray-dark);
}

.notification-list-subtitle {
  font-size: 12px;
  color: var(--ds-gray-light);
}

.mark-all-btn {
  font-size: 11px;
  color: var(--ds-blue-accent);
  background: transparent;
  border: none;
  padding: 2px 6px;
  cursor: pointer;
  border-radius: 4px;
  font-weight: 500;
}

.mark-all-btn:hover {
  background: rgba(62, 142, 115, 0.1);
}

.clear-all-btn {
  font-size: 11px;
  color: #c0392b;
  background: transparent;
  border: none;
  padding: 2px 6px;
  cursor: pointer;
  border-radius: 4px;
  font-weight: 500;
}

.clear-all-btn:hover {
  background: rgba(192, 57, 43, 0.08);
}

.notification-item-action {
  align-self: center;
}

/* Ícone de lixo discreto (visível também em touch); ganha destaque no hover */
.notification-remove-btn {
  opacity: 0.4;
  transition: opacity 0.15s ease;
}

.notification-item:hover .notification-remove-btn,
.notification-remove-btn:focus {
  opacity: 1;
}

.notification-remove-btn:hover .v-icon {
  color: #c0392b !important;
}

.notification-item {
  transition: background 0.15s ease;
  cursor: pointer;
}

.notification-item:hover {
  background-color: var(--ds-gray-bg);
}

.notification-item--unread {
  background-color: var(--ds-blue-50);
}

.notification-item--unread:hover {
  background-color: #ebf5ff;
}

.notification-item-title {
  font-size: 13px !important;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.notification-item-body {
  font-size: 12px !important;
  color: #666 !important;
  margin-top: 2px;
}

.notification-item-date {
  font-size: 11px !important;
  color: var(--ds-gray-light) !important;
  margin-top: 2px;
}

.unread-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ds-blue-accent);
  flex-shrink: 0;
}
</style>
